import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Spin } from 'antd';
import type { Proposal, CreateProposalRequest, Customer } from '../../types/proposal';
import { proposalApi } from '../../services/proposal';

const { TextArea } = Input;

interface ProposalFormProps {
  open: boolean;
  proposalId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ProposalForm: React.FC<ProposalFormProps> = ({
  open,
  proposalId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const isEdit = !!proposalId;

  // 获取客户列表
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await proposalApi.getCustomers({ page: 1, page_size: 100 });
      setCustomers(response.data || []);
    } catch (error) {
      // 错误已处理
    } finally {
      setCustomersLoading(false);
    }
  };

  // 编辑模式下获取提案详情
  useEffect(() => {
    if (open) {
      fetchCustomers();
      if (proposalId) {
        fetchProposalData(proposalId);
      } else {
        form.resetFields();
      }
    }
  }, [open, proposalId]);

  const fetchProposalData = async (id: string) => {
    setFetchLoading(true);
    try {
      const response = await proposalApi.getProposal(id);
      const proposal = response.data;
      form.setFieldsValue({
        title: proposal.title,
        customer_id: proposal.customer_id,
      });
    } catch (error) {
      message.error('获取提案信息失败');
      onClose();
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: CreateProposalRequest = {
        title: values.title,
        customer_id: values.customer_id,
      };

      if (isEdit && proposalId) {
        await proposalApi.updateProposal(proposalId, data);
        message.success('提案更新成功');
      } else {
        await proposalApi.createProposal(data);
        message.success('提案创建成功');
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (!isEdit) {
        message.error('创建提案失败');
      } else {
        message.error('更新提案失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: customer.company_name,
  }));

  return (
    <Modal
      title={isEdit ? '编辑提案' : '新建提案'}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose
      width={600}
    >
      <Spin spinning={fetchLoading}>
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
        <Form.Item
          name="title"
          label="提案标题"
          rules={[{ required: true, message: '请输入提案标题' }]}
        >
          <Input placeholder="请输入提案标题" maxLength={200} />
        </Form.Item>

        <Form.Item
          name="customer_id"
          label="关联客户"
          rules={[{ required: true, message: '请选择关联客户' }]}
        >
          <Select
            placeholder="请选择关联客户"
            loading={customersLoading}
            options={customerOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};
